import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  TrashIcon,
  PlayIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function RecipientDetail() {
  const { recipientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [recipient, setRecipient] = useState(null);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  
  // Message composer state
  const [messageText, setMessageText] = useState('');
  const [sendOption, setSendOption] = useState('now'); // 'now' or 'schedule'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('07:00');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user && recipientId) {
      fetchRecipientData();
    }
  }, [user, recipientId]);

  const fetchRecipientData = async () => {
    try {
      // Fetch recipient details
      const recipientsRes = await axios.get(`${API_URL}/recipients/user/${user.id}`);
      const recipientData = recipientsRes.data.recipients.find(r => r._id === recipientId);
      setRecipient(recipientData);

      // Fetch scheduled messages for this recipient
      const scheduledRes = await axios.get(`${API_URL}/schedule/messages/${user.id}`);
      const recipientScheduled = scheduledRes.data.scheduled_messages.filter(
        m => m.recipient_id === recipientId
      );
      setScheduledMessages(recipientScheduled);

      // Fetch sent messages (delivery logs)
      // TODO: Add API endpoint for delivery logs by recipient
      setSentMessages([]);
      
    } catch (error) {
      console.error('Failed to fetch recipient data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateVoice = async () => {
    if (!messageText.trim()) return;
    
    setGenerating(true);
    try {
      // Get user's voice ID first
      const voiceRes = await axios.get(`${API_URL}/voice/user/${user.id}`);
      
      if (!voiceRes.data.voice_id) {
        alert('❌ Please clone your voice first. Go to Voice Clone to get started.');
        return null;
      }
      
      // Generate voice message using cloned voice
      const response = await axios.post(`${API_URL}/voice/generate`, {
        user_id: user.id,
        text: messageText,
        voice_id: voiceRes.data.voice_id
      });

      // Save as recording
      const recordingRes = await axios.post(`${API_URL}/recordings`, {
        user_id: user.id,
        title: messageText.substring(0, 50),
        transcription: messageText,
        audio_file_url: response.data.audio_url,
        voice_id: voiceRes.data.voice_id,
        duration: response.data.duration || 0
      });

      return recordingRes.data.id;
    } catch (error) {
      console.error('Failed to generate voice:', error);
      if (error.response?.status === 404) {
        alert('❌ Please clone your voice first. Go to Voice Clone in the menu to get started.');
      } else {
        alert('❌ Failed to generate voice message. Please try again.');
      }
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      alert('Please enter a message');
      return;
    }

    setSending(true);
    try {
      // Generate voice message
      const messageId = await handleGenerateVoice();
      if (!messageId) {
        setSending(false);
        return;
      }

      if (sendOption === 'now') {
        // Send immediately via WhatsApp
        await axios.post(`${API_URL}/delivery/send-whatsapp`, {
          message_id: messageId,
          recipient_id: recipientId
        });
        alert('✅ Message sent via WhatsApp!');
      } else {
        // Schedule for later
        const scheduledDateTime = `${scheduleDate}T${scheduleTime}:00`;
        
        // Validate that scheduled time is in the future
        const scheduledDate = new Date(scheduledDateTime);
        const now = new Date();
        
        if (scheduledDate <= now) {
          alert('❌ Scheduled time must be in the future. Please select a later date/time.');
          return;
        }
        
        // Check if more than 1 year in future
        const oneYearFromNow = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
        if (scheduledDate > oneYearFromNow) {
          alert('❌ Cannot schedule messages more than 1 year in advance.');
          return;
        }
        
        await axios.post(`${API_URL}/schedule/message`, null, {
          params: {
            message_id: messageId,
            recipient_id: recipientId,
            scheduled_date: scheduledDateTime,
            delivery_method: 'whatsapp',
            timezone: timezone
          }
        });
        
        const localTime = new Date(scheduledDateTime).toLocaleString('en-US', {
          timeZone: timezone,
          dateStyle: 'medium',
          timeStyle: 'short'
        });
        alert(`✅ Message scheduled successfully!\n\nWill be sent: ${localTime}\nTimezone: ${timezone}`);
      }

      // Reset form
      setMessageText('');
      setShowComposer(false);
      fetchRecipientData();
      
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('❌ Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleCancelScheduled = async (scheduledId) => {
    if (!window.confirm('Cancel this scheduled message?')) return;

    try {
      await axios.delete(`${API_URL}/schedule/${scheduledId}`);
      alert('✅ Message cancelled');
      fetchRecipientData();
    } catch (error) {
      console.error('Failed to cancel:', error);
      alert('❌ Failed to cancel message');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!recipient) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Recipient not found</p>
        <button
          onClick={() => navigate('/recipients')}
          className="text-blue-600 hover:text-blue-700 font-medium"
        >
          ← Back to Family
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/recipients')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 font-medium"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to My Family
        </button>
      </div>

      {/* Recipient Info Card */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 mb-8 text-white shadow-2xl">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-4xl mr-6">
              {recipient.relationship === 'Daughter' ? '👧' :
               recipient.relationship === 'Son' ? '👦' :
               recipient.relationship === 'Spouse' ? '💑' :
               recipient.relationship === 'Parent' ? '👴' : '👤'}
            </div>
            <div>
              <h1 className="text-4xl font-bold mb-2">{recipient.name}</h1>
              <p className="text-blue-100 text-lg mb-1">{recipient.relationship}</p>
              <div className="flex items-center gap-4 text-sm text-blue-200">
                {recipient.email && <span>📧 {recipient.email}</span>}
                {recipient.phone && <span>📱 {recipient.phone}</span>}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{scheduledMessages.filter(m => m.status === 'pending').length}</div>
            <div className="text-blue-200 text-sm">Scheduled</div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="mb-8">
        <button
          onClick={() => setShowComposer(!showComposer)}
          className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg"
        >
          <PlusIcon className="h-6 w-6" />
          Write New Message
        </button>
      </div>

      {/* Message Composer */}
      {showComposer && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">New Message for {recipient.name}</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Message Text
              </label>
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type your message here... It will be converted to your voice."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                disabled={generating || sending}
              />
              <p className="text-xs text-gray-500 mt-1">
                Your message will be generated in your cloned voice
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                When to send?
              </label>
              <div className="space-y-3">
                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
                  <input
                    type="radio"
                    value="now"
                    checked={sendOption === 'now'}
                    onChange={(e) => setSendOption(e.target.value)}
                    className="h-5 w-5 text-blue-600"
                    disabled={generating || sending}
                  />
                  <div className="ml-3">
                    <div className="font-semibold text-gray-900">Send Now</div>
                    <div className="text-sm text-gray-600">Deliver immediately via WhatsApp</div>
                  </div>
                </label>

                <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
                  <input
                    type="radio"
                    value="schedule"
                    checked={sendOption === 'schedule'}
                    onChange={(e) => setSendOption(e.target.value)}
                    className="h-5 w-5 text-blue-600 mt-1"
                    disabled={generating || sending}
                  />
                  <div className="ml-3 flex-1">
                    <div className="font-semibold text-gray-900 mb-2">Schedule for Later</div>
                    {sendOption === 'schedule' && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                            <input
                              type="date"
                              value={scheduleDate}
                              onChange={(e) => setScheduleDate(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              disabled={generating || sending}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                            <input
                              type="time"
                              value={scheduleTime}
                              onChange={(e) => setScheduleTime(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              disabled={generating || sending}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Your Timezone</label>
                          <select
                            value={timezone}
                            onChange={(e) => setTimezone(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                            disabled={generating || sending}
                          >
                            <option value="America/New_York">Eastern Time (ET)</option>
                            <option value="America/Chicago">Central Time (CT)</option>
                            <option value="America/Denver">Mountain Time (MT)</option>
                            <option value="America/Los_Angeles">Pacific Time (PT)</option>
                            <option value="America/Anchorage">Alaska Time (AKT)</option>
                            <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                            <option value="Europe/London">London (GMT/BST)</option>
                            <option value="Europe/Paris">Paris (CET/CEST)</option>
                            <option value="Asia/Dubai">Dubai (GST)</option>
                            <option value="Asia/Kolkata">India (IST)</option>
                            <option value="Asia/Singapore">Singapore (SGT)</option>
                            <option value="Asia/Tokyo">Tokyo (JST)</option>
                            <option value="Australia/Sydney">Sydney (AEDT/AEST)</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Currently: {timezone}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setShowComposer(false);
                  setMessageText('');
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                disabled={generating || sending}
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!messageText.trim() || generating || sending || (sendOption === 'schedule' && !scheduleDate)}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Generating Voice...
                  </>
                ) : sending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-5 w-5" />
                    {sendOption === 'now' ? 'Send Now' : 'Schedule Message'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled Messages */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 mb-8">
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">📅 Scheduled Messages</h2>
          <p className="text-sm text-gray-600 mt-1">
            {scheduledMessages.filter(m => m.status === 'pending').length} upcoming messages
          </p>
        </div>

        {scheduledMessages.filter(m => m.status === 'pending').length === 0 ? (
          <div className="p-12 text-center">
            <CalendarDaysIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Scheduled Messages</h3>
            <p className="text-gray-600 mb-6">
              Write a message and schedule it for a special date
            </p>
            <button
              onClick={() => setShowComposer(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all"
            >
              Write First Message
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {scheduledMessages
              .filter(m => m.status === 'pending')
              .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
              .map((msg) => (
                <div key={msg.scheduled_id} className="p-6 hover:bg-gray-50 transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {msg.message_title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <CalendarDaysIcon className="h-4 w-4" />
                          <span>{new Date(msg.scheduled_date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-4 w-4" />
                          <span>{new Date(msg.scheduled_date).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                        </div>
                        {msg.timezone && (
                          <span className="text-xs text-gray-500">
                            {msg.timezone.replace('_', ' ')}
                          </span>
                        )}
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          WhatsApp
                        </span>
                      </div>
                      {msg.metadata?.deployment_schedule && (
                        <div className="mt-2 text-xs text-blue-600 font-medium">
                          🎖️ Deployment Day {msg.metadata.day_number}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleCancelScheduled(msg.scheduled_id)}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Cancel"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Sent Messages */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">✅ Sent Messages</h2>
          <p className="text-sm text-gray-600 mt-1">
            {scheduledMessages.filter(m => m.status === 'sent').length} messages delivered
          </p>
        </div>

        {scheduledMessages.filter(m => m.status === 'sent').length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No messages sent yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {scheduledMessages
              .filter(m => m.status === 'sent')
              .sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date))
              .map((msg) => (
                <div key={msg.scheduled_id} className="p-6 hover:bg-gray-50 transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {msg.message_title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          <span>Sent {new Date(msg.scheduled_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
